import { Button, ButtonProps, Icon, Skeleton, Stack, Typography } from '@mui/material';
import {
  AggregateSessionScope,
  AttendanceSessionEnum,
  DEFAULT_DATE_FORMAT_FNS,
  useGetGroupAttendanceAggregateQuery,
} from '@schooly/api';
import { Attention2Icon, ContextMenu, DoneIcon, PlusIcon } from '@schooly/style';
import { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useNotifications } from '@schooly/components/notifications';
import { useAuth } from '@schooly/components/authentication';
import {
  AttendanceStatsBadge,
  findSessionEntry,
  mapGroupAttendanceAggregateResponseToAttendanceStats,
} from '../../TakeAttendanceModal/AttendanceStatsBadge';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import {
  buildTrackingPoints,
  selectPeriodsToTake,
} from '../../../Attendance/AttendanceTable/common/trackingPoints';
import { AttendanceGridTrackingPoint } from '../../../Attendance/AttendanceTable/common/types';
import {
  takeAttendancePath,
  takeAttendancePathForPoint,
} from '../../TakeAttendanceModal/take-attendance-utils';
import {
  AGGREGATE_INCLUDE_ALL_POINTS,
  AGGREGATE_INCLUDE_AM_PM,
} from '../../../Attendance/AttendanceTable/common/constants';

import {
  getAttendanceFrequencyConfig,
  resolveAttendanceSessionState,
} from '../../../Attendance/attendance-utils';

export const GroupPreviewModalAttendanceTakeButtons: FC<{
  ageGroupId: string;
  isTutorGroup: boolean;
  canTakeAttendance: boolean;
  groupId: string;
}> = ({ canTakeAttendance, groupId, isTutorGroup, ageGroupId }) => {
  const { schoolId = '' } = useAuth();
  const { showError } = useNotifications();
  const { formatMessage } = useIntl();
  const { activeAgeGroups } = useAgeGroups({
    schoolId,
    userType: SchoolUserRole.Student,
  });

  const { isSubjectResponsible } = getAttendanceFrequencyConfig(
    activeAgeGroups.find((ag) => ag.id === ageGroupId)?.attendance_frequency,
  );

  const date = format(new Date(), DEFAULT_DATE_FORMAT_FNS);
  const { data } = useGetGroupAttendanceAggregateQuery(
    {
      groupId,
      school_id: schoolId,
      date_from: date,
      date_to: date,
      session_scope: isTutorGroup ? AggregateSessionScope.All : AggregateSessionScope.Group,
      include: isTutorGroup ? AGGREGATE_INCLUDE_AM_PM : AGGREGATE_INCLUDE_ALL_POINTS,
    },
    {
      refetchOnMount: 'always',
      enabled: !!schoolId,
      onError: showError,
    },
  );

  const apiTrackingPoints = data?.total.week_days[0]?.tracking_points ?? [];
  const trackingPoints = useMemo(() => buildTrackingPoints(apiTrackingPoints), [apiTrackingPoints]);
  const periodsToTake = useMemo(() => selectPeriodsToTake(apiTrackingPoints), [apiTrackingPoints]);

  const renderCardForSession = (session: AttendanceSessionEnum) => {
    // The session's own tracking point, which may be a slot coupled to a period
    // (label "PM/3") — taking that slot is how this session gets marked.
    const point = trackingPoints.find((item) => item.session === session);

    const sessionValues = (data?.rows ?? []).map((row) => findSessionEntry(row.cells, session));
    const isCompleted =
      !!sessionValues.length && !sessionValues.some((v) => !v?.attendance_code_id);

    const state = resolveAttendanceSessionState({
      isTutorGroup,
      sessionExists: !!point,
      isCompleted,
      isSubjectResponsible: isSubjectResponsible[session],
    });

    if (!state || !point) return null;
    if (state === 'subject_teacher') return renderTakeAttendanceInSubjectCard(session);

    return renderTakeAttendanceCard(session, point, state === 'completed');
  };

  const renderTakeAttendanceCard = (
    session: AttendanceSessionEnum,
    point: AttendanceGridTrackingPoint,
    isCompleted: boolean,
  ) => {
    return (
      <AttendanceStatsContainer
        isSuccess={isCompleted}
        title={
          <FormattedMessage
            id={isCompleted ? 'attendance-SessionCompleted' : 'attendance-SessionRequired'}
            values={{ session: formatMessage({ id: `attendance-${session.toUpperCase()}` }) }}
          />
        }
      >
        {isCompleted ? (
          <AttendanceStatsBadge
            computeStats={mapGroupAttendanceAggregateResponseToAttendanceStats({
              session,
              dayAggregate: data,
            })}
          />
        ) : (
          <TakeAttendanceButton
            point={point}
            date={date}
            groupId={groupId}
            canTakeAttendance={canTakeAttendance}
          />
        )}
      </AttendanceStatsContainer>
    );
  };

  const renderTakeAttendanceInSubjectCard = (session: AttendanceSessionEnum) => {
    return (
      <AttendanceStatsContainer
        isSuccess={false}
        title={
          <FormattedMessage
            id="attendance-SessionRequiredBySubjectTeacher"
            values={{ session: formatMessage({ id: `attendance-${session.toUpperCase()}` }) }}
          />
        }
      />
    );
  };

  if (!data)
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={64}
        sx={(theme) => ({ '&&': { borderRadius: theme.spacing(1) } })}
      />
    );

  return (
    <Stack flexDirection="row" gap={1.5} alignItems="center">
      {renderCardForSession(AttendanceSessionEnum.Am)}
      {renderCardForSession(AttendanceSessionEnum.Pm)}
      {!!periodsToTake.length && (
        <AttendanceStatsContainer
          isSuccess={false}
          title={<FormattedMessage id="attendance-PeriodRequired" />}
        >
          <TakePeriodAttendanceMenu
            periods={periodsToTake}
            date={date}
            groupId={groupId}
            canTakeAttendance={canTakeAttendance}
          />
        </AttendanceStatsContainer>
      )}
    </Stack>
  );
};

const TakePeriodAttendanceMenu: FC<{
  groupId: string;
  periods: { id: string; name: string }[];
  date: string;
  canTakeAttendance: boolean;
}> = ({ periods, date, groupId, canTakeAttendance }) => {
  const navigate = useNavigate();

  if (!canTakeAttendance) return null;

  return (
    <ContextMenu
      placement="bottom-end"
      arrow={false}
      componentsProps={{
        tooltip: {
          sx: {
            minWidth: 200,
            '&&.MuiTooltip-tooltip': {
              mt: 0.25,
            },
            '.container': {
              gap: 1.5,
              paddingX: 0,
            },
          },
        },
      }}
      actions={periods.map((period) => ({
        label: period.name,
        icon: <PlusIcon />,
        onClick: (close) => {
          close?.();
          navigate(takeAttendancePath({ groupId, date, session: null, periodId: period.id }));
        },
      }))}
    >
      {(open) => (
        <Stack>
          <TakeCardButton onClick={open}>
            <FormattedMessage id="attendance-Take" />
          </TakeCardButton>
        </Stack>
      )}
    </ContextMenu>
  );
};

const AttendanceStatsContainer: FC<PropsWithChildren<{ isSuccess: boolean; title: ReactNode }>> = ({
  isSuccess,
  title,
  children,
}) => {
  return (
    <Stack
      px={1.5}
      py={1.5}
      flex={1}
      borderRadius={1}
      bgcolor={isSuccess ? 'success.superLight' : 'common.orange5'}
      border={1}
      borderColor={isSuccess ? 'success.main' : 'warning.main'}
      flexDirection="row"
      gap={1}
      justifyContent="space-between"
      alignItems="center"
    >
      <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
        <Stack
          width={40}
          height={40}
          borderRadius="100%"
          justifyContent={'center'}
          alignItems={'center'}
          bgcolor={'white'}
        >
          {isSuccess ? (
            <Icon color="success">
              <DoneIcon />
            </Icon>
          ) : (
            <Icon
              sx={(theme) => ({
                color: theme.palette.background.paper,
                path: { color: theme.palette.common.orange },
              })}
            >
              <Attention2Icon />
            </Icon>
          )}
        </Stack>
        <Typography variant="h3" color={isSuccess ? 'success.main' : 'warning.main'}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Stack>
  );
};

/** The white pill button that sits inside an attendance card. */
const TakeCardButton: FC<PropsWithChildren<{ onClick: ButtonProps['onClick'] }>> = ({
  onClick,
  children,
}) => (
  <Button
    size="small"
    sx={(theme) => ({
      backgroundColor: 'white',
      color: theme.palette.primary.main,
      border: '1px solid white',
      transition: 'all .2s ease',

      '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.text.disabled,
        borderColor: theme.palette.text.disabled,
      },
    })}
    startIcon={<PlusIcon />}
    onClick={onClick}
  >
    {children}
  </Button>
);

const TakeAttendanceButton: FC<{
  groupId: string;
  /** The point to take: "AM", "PM", or "PM/3" when coupled to a period. */
  point: AttendanceGridTrackingPoint;
  date: string;
  canTakeAttendance: boolean;
}> = ({ point, date, groupId, canTakeAttendance }) => {
  const navigate = useNavigate();

  if (!canTakeAttendance) return null;

  return (
    <TakeCardButton onClick={() => navigate(takeAttendancePathForPoint(groupId, date, point))}>
      <FormattedMessage id="attendance-TakeSession" values={{ trackingPoint: point.label }} />
    </TakeCardButton>
  );
};
